import React, { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { listData, updateData, deleteData, listCampaigns, shopifyWriteApply, socialPublish, regenerateApproval, regeneratePost, regenerateItemImage, campaignSummary, scoreAsset, videoFromPost, videoJobStatus, socialStatus, cleanupApprovals, qcImprove, qcApplyRevision, qcReadiness, pollImageJob, mediaAttach } from "@/lib/api";
import { MediaPicker } from "@/components/MediaPicker";
import { MarketingScoreDialog, gradeColor } from "@/components/MarketingScore";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { ImproveCompareDialog } from "@/components/ImproveCompareDialog";
import { useClient } from "@/context/ClientContext";
import { PageHeader, PageContainer } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import {
  CheckCircle, XCircle, PaperPlaneTilt, CalendarPlus, Rocket, Trash, Clock,
  ChatCircleDots, ClockCounterClockwise, FolderSimple, ListChecks, Storefront,
  PencilSimple, ArrowsClockwise, FloppyDisk, FacebookLogo, InstagramLogo, Warning, PlugsConnected,
  Broom, Funnel, ArrowSquareOut, ImageBroken, Copy, ArrowRight, Info, Sparkle, RocketLaunch, UserCircle, ImageSquare,
} from "@phosphor-icons/react";
import { Checkbox } from "@/components/ui/checkbox";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { publishDate, fmtPublish } from "@/components/ItemDetailSheet";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const COLUMNS = [
  { key: "draft", label: "Draft", color: "text-[#9a9a9a]" },
  { key: "pending", label: "Pending Approval", color: "text-amber-400" },
  { key: "approved", label: "Approved", color: "text-emerald-400" },
  { key: "scheduled", label: "Scheduled", color: "text-sky-400" },
  { key: "published", label: "Published", color: "brand-text" },
  { key: "simulated", label: "Simulated", color: "text-amber-400" },
  { key: "failed", label: "Failed", color: "text-red-400" },
];
const STATUS_LABEL = { draft: "Draft", pending: "Pending", approved: "Approved", scheduled: "Scheduled", published: "Published", simulated: "Simulated", failed: "Failed", rejected: "Rejected", coming_soon: "Coming soon" };
const today = () => new Date().toISOString().slice(0, 10);
const nowISO = () => new Date().toISOString();
const appendHistory = (item, to) => [...(item.history || []), { from: item.status, to, at: nowISO() }];
const TZ_LABEL = Intl.DateTimeFormat().resolvedOptions().timeZone;
const localParts = (d) => {
  const p = (n) => String(n).padStart(2, "0");
  return { date: `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`, time: `${p(d.getHours())}:${p(d.getMinutes())}` };
};
// "Jul 15, 2026 • 10:35 AM" — date-only strings (legacy published_at) render without a time
const fmtStamp = (iso) => {
  if (!iso) return null;
  const dateOnly = String(iso).length <= 10;
  const d = dateOnly ? new Date(`${iso}T12:00:00`) : new Date(iso);
  if (isNaN(d)) return iso;
  const date = d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  return dateOnly ? date : `${date} • ${d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}`;
};
const publishedBy = (item) =>
  item.publish_result?.published_by || (item.meta?.autopilot ? "Vozelto Auto Pilot" : "Manual (Vozelto user)");
const copyText = (text, msg) => {
  try {
    const p = navigator.clipboard?.writeText(String(text));
    if (p?.then) p.then(() => toast.success(msg)).catch(() => toast.error("Copy blocked by browser"));
    else toast.success(msg);
  } catch { toast.error("Copy blocked by browser"); }
};
const isMetaItem = (item) => ["Facebook", "Instagram"].includes(item.platform)
  || (item.platform || "").toLowerCase().includes("facebook")
  || (item.platform || "").toLowerCase().includes("instagram")
  || item.type === "social" || item.type === "instagram_post";

const REWRITE_OPTIONS = [
  { l: "Rewrite Copy", d: "Rewrite the copy from scratch with a fresh angle" },
  { l: "Stronger Call To Action", d: "Make the call to action significantly stronger and more compelling" },
  { l: "More Sales Focused", d: "Make it more sales-focused and conversion-driven" },
  { l: "More Premium", d: "Make the tone more premium and refined" },
  { l: "More Casual", d: "Make the tone more casual and friendly" },
  { l: "More Humorous", d: "Add tasteful humor and wit while staying on-brand" },
  { l: "More Educational", d: "Make it more educational and value-packed" },
  { l: "Shorter", d: "Make it significantly shorter and punchier" },
  { l: "Longer", d: "Expand it with more detail and depth" },
  { l: "More Luxury", d: "Give it a luxury, high-end feel" },
  { l: "More Urgency", d: "Add urgency and scarcity to drive immediate action" },
];
const TRANSLATE_OPTIONS = [
  { l: "Translate to Spanish", d: "Translate the text to Spanish, keeping the tone, formatting and any URLs" },
  { l: "Translate to French", d: "Translate the text to French, keeping the tone, formatting and any URLs" },
  { l: "Translate to German", d: "Translate the text to German, keeping the tone, formatting and any URLs" },
];
const PLATFORM_FILTERS = ["all", "Facebook", "Instagram", "LinkedIn", "TikTok", "Email", "SMS", "Blog"];
const relDay = (d) => {
  const now = new Date();
  const t0 = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const d0 = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const diff = Math.round((d0 - t0) / 86400000);
  if (diff === 0) return "Today";
  if (diff === 1) return "Tomorrow";
  if (diff < 7) return d.toLocaleDateString("en-US", { weekday: "long" });
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

export default function ApprovalQueue() {
  const { activeId, activeClient } = useClient();
  const { user } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const key = ["data", "approvals", activeId];
  const { data: items = [] } = useQuery({ queryKey: key, queryFn: () => listData("approvals", activeId), enabled: !!activeId });
  const { data: campaigns = [] } = useQuery({ queryKey: ["campaigns", activeId], queryFn: () => listCampaigns(activeId), enabled: !!activeId });
  const { data: social } = useQuery({ queryKey: ["social-status", activeId], queryFn: () => socialStatus(activeId), enabled: !!activeId });

  // Audit stamp for manual (non-Meta) publishes — email/blog items with no external provider
  const manualPub = () => ({ published_at: today(), publish_result: {
    status: "published", verification_status: "manual", at: nowISO(), simulated: true,
    published_by: user?.display_name || user?.name || user?.email || "Manual (Vozelto user)" } });

  // Active Meta publishing destination for this workspace (safety banner).
  const socialDest = useMemo(() => {
    if (!social?.connected) return null;
    const pages = social.pages || [];
    const page = pages.find((p) => p.id === social.selected_page) || null;
    return {
      testMode: !!social.test_mode,
      pageName: page?.name || null,
      igId: page?.instagram_business_account_id || null,
      igHandle: page?.instagram_username || null,
      needsPage: !social.test_mode && pages.length > 0 && !page,
    };
  }, [social]);

  const [schedFor, setSchedFor] = useState(null);
  const [schedDate, setSchedDate] = useState("");
  const [schedTime, setSchedTime] = useState("");
  const [groupByCampaign, setGroupByCampaign] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [detailId, setDetailId] = useState(null);
  const [mediaPickFor, setMediaPickFor] = useState(null);
  const [comment, setComment] = useState("");
  const [editing, setEditing] = useState(false);
  const [editBody, setEditBody] = useState("");
  // Filters
  const [platformFilter, setPlatformFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [employeeFilter, setEmployeeFilter] = useState("all");
  const [objectiveFilter, setObjectiveFilter] = useState("all");
  // Cleanup dialog
  const [cleanupOpen, setCleanupOpen] = useState(false);
  const [qcBusyId, setQcBusyId] = useState(null);
  const [qcCompare, setQcCompare] = useState(null);
  const [readyBusyId, setReadyBusyId] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [historyItem, setHistoryItem] = useState(null);
  const [cleanupSel, setCleanupSel] = useState({ draft: true, pending: true, approved: true, scheduled: true, failed: true, published: false });
  // Bulk actions V2
  const [confirmAction, setConfirmAction] = useState(null);
  const [reschedOpen, setReschedOpen] = useState(false);
  const [reschedDate, setReschedDate] = useState("");
  const [reschedTime, setReschedTime] = useState("09:00");
  const [completionDismissed, setCompletionDismissed] = useState(false);
  const [scoreBusyId, setScoreBusyId] = useState(null);
  const [scoreView, setScoreView] = useState(null); // {title, score}

  const platformOf = (i) => {
    const p = (i.platform || "").toLowerCase();
    if (p.includes("instagram") || i.type === "instagram_post") return "Instagram";
    if (p === "email" || i.type === "email") return "Email";
    if (p === "sms" || i.type === "sms") return "SMS";
    if (p === "blog" || i.type === "blog") return "Blog";
    if (p.includes("facebook") || i.type === "social") return "Facebook";
    return i.platform || "Other";
  };

  const inv = () => qc.invalidateQueries({ queryKey: key });
  const campaignName = useMemo(() => Object.fromEntries(campaigns.map((c) => [c.id, c.name])), [campaigns]);
  const detail = items.find((i) => i.id === detailId) || null;

  const employees = useMemo(() => [...new Set(items.map((i) => i.created_by).filter(Boolean))].sort(), [items]);
  const objectives = useMemo(() => [...new Set(items.map((i) => i.meta?.objective).filter(Boolean))].sort(), [items]);

  const statusCounts = useMemo(() => {
    const c = {};
    items.forEach((i) => { c[i.status] = (c[i.status] || 0) + 1; });
    return c;
  }, [items]);

  const nextScheduled = useMemo(() => {
    const now = new Date();
    const upcoming = items
      .filter((i) => i.status === "scheduled" && i.scheduled_at)
      .map((i) => ({ i, d: publishDate(i) }))
      .filter((x) => x.d && x.d >= now)
      .sort((a, b) => a.d - b.d);
    const nextOf = (pred) => upcoming.find((x) => pred(x.i)) || null;
    return {
      post: nextOf((i) => isMetaItem(i)),
      email: nextOf((i) => platformOf(i) === "Email"),
      sms: nextOf((i) => platformOf(i) === "SMS"),
      blog: nextOf((i) => platformOf(i) === "Blog"),
    };
  }, [items]); // eslint-disable-line

  // Latest fully-finished campaign (all items published/simulated/failed, at least one live)
  const completeCampaignId = useMemo(() => {
    const done = new Set(["published", "simulated", "failed"]);
    const byC = {};
    items.forEach((i) => { if (i.campaign_id) (byC[i.campaign_id] = byC[i.campaign_id] || []).push(i); });
    const ids = Object.keys(byC).filter((cid) => byC[cid].length >= 3
      && byC[cid].every((i) => done.has(i.status))
      && byC[cid].some((i) => ["published", "simulated"].includes(i.status)));
    ids.sort((a, b) => ((byC[b][0].created_at || "").localeCompare(byC[a][0].created_at || "")));
    return ids[0] || null;
  }, [items]);
  const { data: completionSummary } = useQuery({
    queryKey: ["campaign-summary", completeCampaignId],
    queryFn: () => campaignSummary(completeCampaignId),
    enabled: !!completeCampaignId && !completionDismissed,
  });

  const filteredItems = useMemo(() => items.filter((i) => {
    if (platformFilter !== "all" && platformOf(i) !== platformFilter) return false;
    if (statusFilter !== "all" && i.status !== statusFilter) return false;
    if (employeeFilter !== "all" && i.created_by !== employeeFilter) return false;
    if (objectiveFilter !== "all" && i.meta?.objective !== objectiveFilter) return false;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      if (!`${i.title || ""} ${i.body || ""} ${i.hashtags || ""}`.toLowerCase().includes(q)) return false;
    }
    return true;
  }), [items, platformFilter, statusFilter, employeeFilter, objectiveFilter, search]); // eslint-disable-line

  const setStatus = useMutation({
    mutationFn: ({ id, patch }) => updateData("approvals", id, patch),
    onSuccess: inv,
  });
  const delM = useMutation({ mutationFn: (id) => deleteData("approvals", id), onSuccess: () => { inv(); toast.success("Removed"); } });
  const cleanup = useMutation({
    mutationFn: () => cleanupApprovals({
      client_id: activeId,
      statuses: Object.entries(cleanupSel).filter(([k, v]) => v && k !== "published").map(([k]) => k),
      include_published: !!cleanupSel.published,
    }),
    onSuccess: (r) => { inv(); clearSel(); setCleanupOpen(false); toast.success(`Cleared ${r.deleted} item(s)`); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Cleanup failed"),
  });
  const applyShopify = useMutation({
    mutationFn: (id) => shopifyWriteApply(id),
    onSuccess: (r) => { inv(); toast.success(r.status === "applied" ? "Applied to Shopify ✓" : "Applied (simulation — Shopify not connected)"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Apply failed"),
  });
  const publishSocial = useMutation({
    mutationFn: (id) => socialPublish(id),
    onSuccess: (r) => { inv(); toast.success(r.test_mode ? "Published (TEST MODE — simulated, not live)" : "Published to Meta ✓"); },
    onError: (e) => { inv(); toast.error(e?.response?.data?.detail || "Publish failed"); },
  });

  const saveEdit = useMutation({
    mutationFn: ({ id, item }) => updateData("approvals", id, {
      body: editBody,
      history: [...(item.history || []), { from: item.status, to: item.status, at: nowISO(), note: "edited" }],
    }),
    onSuccess: () => { inv(); setEditing(false); toast.success("Changes saved"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Save failed"),
  });
  const regen = useMutation({
    mutationFn: (id) => regenerateApproval(id),
    onSuccess: (r) => { inv(); if (r.body) setEditBody(r.body); toast.success(r.image_path ? "Image regenerated" : "Content regenerated with AI"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Regenerate failed"),
  });

  // reset editor state when the detail item changes
  React.useEffect(() => {
    setEditing(false);
    setEditBody(detail?.body || "");
  }, [detailId]); // eslint-disable-line

  const move = (item, status, extra = {}) => {
    setStatus.mutate({ id: item.id, patch: { status, history: appendHistory(item, status), ...extra } });
    toast.success(`Moved to ${STATUS_LABEL[status] || status}`);
  };
  const openSched = (item) => {
    const existing = item.status === "scheduled" ? publishDate(item) : null;
    const { date, time } = localParts(existing || new Date(Date.now() + 15 * 60000));
    setSchedFor(item); setSchedDate(date); setSchedTime(time);
  };
  const quickSched = (mins) => {
    const { date, time } = localParts(new Date(Date.now() + mins * 60000));
    setSchedDate(date); setSchedTime(time);
  };
  const confirmSchedule = () => {
    if (!schedDate) return toast.error("Pick a date");
    const local = new Date(`${schedDate}T${schedTime || "09:00"}:00`);
    if (isNaN(local)) return toast.error("Invalid date/time");
    if (local.getTime() < Date.now() - 60000) return toast.error("Pick a time in the future — or use the Now button");
    const iso = local.toISOString();
    const editing = schedFor.status === "scheduled";
    setStatus.mutate({ id: schedFor.id, patch: {
      status: "scheduled", history: appendHistory(schedFor, "scheduled"),
      scheduled_at: iso.slice(0, 10), "meta.post_time": iso.slice(11, 16) } });
    const f = fmtPublish(local);
    toast.success(`${editing ? "Schedule updated" : "Scheduled"} — ${f.date}, ${f.time} ${f.tz}`);
    setSchedFor(null); setSchedDate(""); setSchedTime("");
  };

  // --- bulk actions ---
  const toggleSel = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const clearSel = () => setSelected(new Set());
  const selItems = items.filter((i) => selected.has(i.id));
  const bulk = async (status, extra = {}) => {
    await Promise.all(selItems.map((it) => updateData("approvals", it.id, { status, history: appendHistory(it, status), ...extra })));
    inv(); clearSel(); toast.success(`${selItems.length} moved to ${STATUS_LABEL[status] || status}`);
  };
  const bulkDelete = async () => {
    await Promise.all(selItems.map((it) => deleteData("approvals", it.id)));
    inv(); clearSel(); toast.success(`${selItems.length} removed`);
  };
  // Bulk publish NEVER bypasses the Meta pipeline — FB/IG go through the real publish API
  const bulkPublish = async () => {
    const metaSel = selItems.filter(isMetaItem);
    const others = selItems.filter((it) => !isMetaItem(it));
    const results = await Promise.all([
      ...metaSel.map((it) => socialPublish(it.id).then(() => true)
        .catch((e) => { toast.error(`${it.title || "Post"}: ${e?.response?.data?.detail || "publish failed"}`); return false; })),
      ...others.map((it) => updateData("approvals", it.id, { status: "published", history: appendHistory(it, "published"), ...manualPub() }).then(() => true)),
    ]);
    inv(); clearSel();
    const ok = results.filter(Boolean).length;
    if (ok) toast.success(`${ok}/${results.length} item(s) processed`);
  };

  // --- comments ---
  const addComment = () => {
    if (!comment.trim() || !detail) return;
    const comments = [...(detail.comments || []), { text: comment.trim(), at: nowISO() }];
    setStatus.mutate({ id: detail.id, patch: { comments } }, { onSuccess: () => toast.success("Comment added") });
    setComment("");
  };

  // ---- AI Quality Control: Creative Director second pass + Campaign Readiness ----
  const [rewriteBusyId, setRewriteBusyId] = useState(null);
  const [regenPostBusyId, setRegenPostBusyId] = useState(null);

  const runRewrite = async (item, opt) => {
    setRewriteBusyId(item.id);
    toast.loading(`${opt.l}…`, { id: "rewrite" });
    try {
      await regenerateApproval(item.id, opt.d, "rewrite");
      toast.dismiss("rewrite");
      toast.success(`${opt.l} — copy updated`);
      inv();
    } catch (e) { toast.dismiss("rewrite"); toast.error(e?.response?.data?.detail || `${opt.l} failed — try again`); }
    setRewriteBusyId(null);
  };

  const runRegenPost = async (item) => {
    setRegenPostBusyId(item.id);
    toast.loading("Regenerating this post — fresh copy + art-directed image…", { id: "regenpost" });
    try {
      const { job_id } = await regeneratePost(item.id);
      const job = await pollImageJob(job_id, (j) => { if (j.current) toast.loading(`${j.current}…`, { id: "regenpost" }); });
      toast.dismiss("regenpost");
      if (job.status === "error") toast.error(job.error || "Regenerate failed — try again");
      else if (job.result?.image_error) toast.warning(`Copy regenerated — image failed: ${job.result.image_error}`);
      else toast.success("Post regenerated — fresh copy and image");
      inv();
    } catch (e) { toast.dismiss("regenpost"); toast.error(e?.response?.data?.detail || "Regenerate failed — try again"); }
    setRegenPostBusyId(null);
  };

  const runRegenImageOnly = async (item) => {
    setRegenPostBusyId(item.id);
    toast.loading("Regenerating image only — caption untouched…", { id: "regenimg" });
    try {
      await regenerateItemImage(item.id);
      toast.dismiss("regenimg");
      toast.success("Fresh art-directed image generated");
      inv();
    } catch (e) { toast.dismiss("regenimg"); toast.error(e?.response?.data?.detail || "Image regeneration failed"); }
    setRegenPostBusyId(null);
  };

  const runRegenCaptionOnly = async (item) => {
    setRewriteBusyId(item.id);
    toast.loading("Regenerating caption only — image untouched…", { id: "regencap" });
    try {
      await regenerateApproval(item.id);
      toast.dismiss("regencap");
      toast.success("Fresh caption written");
      inv();
    } catch (e) { toast.dismiss("regencap"); toast.error(e?.response?.data?.detail || "Caption regeneration failed"); }
    setRewriteBusyId(null);
  };

  // ---- Bulk Actions V2 (with confirmations) ----
  const runScore = async (item) => {
    setScoreBusyId(item.id);
    toast.loading("AI is auditing this asset…", { id: "score" });
    try {
      const score = await scoreAsset(item.id);
      toast.dismiss("score");
      setScoreView({ title: item.title, score });
      inv();
    } catch (e) { toast.dismiss("score"); toast.error(e?.response?.data?.detail || "Scoring failed — try again"); }
    setScoreBusyId(null);
  };

  const getSelected = () => filteredItems.filter((i) => selected.has(i.id));
  const approveMany = async (list, label) => {
    for (const it of list) await updateData("approvals", it.id, { status: "approved", history: appendHistory(it, "approved") });
    inv(); clearSel();
    toast.success(`${list.length} item(s) approved${label ? ` — ${label}` : ""}${list.some((i) => i.meta?.auto_schedule) ? " & scheduled" : ""}`);
  };
  const rejectMany = async (list) => {
    for (const it of list) await updateData("approvals", it.id, { status: "draft", history: appendHistory(it, "draft") });
    inv(); clearSel();
    toast.success(`${list.length} item(s) rejected (moved to Draft)`);
  };
  const askApproveAll = (pred, label) => {
    const list = filteredItems.filter((i) => i.status === "pending" && (!pred || pred(i)));
    if (!list.length) return toast.info(`No pending ${label || "items"} to approve`);
    setConfirmAction({
      title: `Approve ${label || "All Pending"}`,
      message: `Approve ${list.length === 1 ? "this marketing asset" : `all ${list.length} marketing assets`} and schedule them for publishing?`,
      cta: "Approve", fn: () => approveMany(list, label),
    });
  };
  const askApproveSelected = () => {
    const list = getSelected();
    if (!list.length) return toast.info("Select items first (checkboxes)");
    setConfirmAction({ title: "Approve Selected", cta: "Approve",
      message: `Approve ${list.length} selected asset(s) and schedule them for publishing?`, fn: () => approveMany(list, "") });
  };
  const askRejectSelected = () => {
    const list = getSelected();
    if (!list.length) return toast.info("Select items first (checkboxes)");
    setConfirmAction({ title: "Reject Selected", cta: "Reject",
      message: `Reject ${list.length} selected asset(s)? They move back to Draft for editing.`, fn: () => rejectMany(list) });
  };
  const applyReschedule = async () => {
    if (!reschedDate) return toast.error("Pick a date first");
    const list = getSelected();
    if (!list.length) return toast.info("Select items first (checkboxes)");
    const iso = new Date(`${reschedDate}T${reschedTime || "09:00"}`).toISOString();
    for (const it of list) {
      await updateData("approvals", it.id, {
        scheduled_at: iso,
        ...(["approved", "scheduled"].includes(it.status) ? { status: "scheduled", history: appendHistory(it, "scheduled") } : {}),
      });
    }
    inv(); clearSel(); setReschedOpen(false);
    toast.success(`${list.length} item(s) rescheduled`);
  };

  const runVideoFromPost = async (item) => {
    setRegenPostBusyId(item.id);
    toast.loading("Planning + rendering a promo video from this post (1-3 min)…", { id: "vfp" });
    try {
      const { job_id } = await videoFromPost(item.id);
      let job = { status: "rendering" };
      for (let i = 0; i < 60 && !["done", "failed"].includes(job.status); i++) {
        await new Promise((r) => setTimeout(r, 5000));
        job = await videoJobStatus(job_id);
        if (job.step) toast.loading(`Video: ${job.step}…`, { id: "vfp" });
      }
      toast.dismiss("vfp");
      if (job.status === "done") { toast.success("Promo video created — it's in the queue as a new pending item"); inv(); }
      else toast.error(job.error || "Video render did not finish — check AI Video Studio");
    } catch (e) { toast.dismiss("vfp"); toast.error(e?.response?.data?.detail || "Video generation failed"); }
    setRegenPostBusyId(null);
  };

  const runQcImprove = async (item) => {
    setQcBusyId(item.id);
    toast.loading("Creative Director's second pass… (~1-2 min)", { id: "qc" });
    try {
      const { job_id } = await qcImprove(item.id);
      const job = await pollImageJob(job_id, (j) => { if (j.current) toast.loading(`${j.current}…`, { id: "qc" }); });
      toast.dismiss("qc");
      if (job.status === "error") toast.error(job.error || "Improve pass failed — try again");
      else setQcCompare(job.result);
      inv();
    } catch (e) { toast.dismiss("qc"); toast.error(e?.response?.data?.detail || "Improve pass failed — try again"); }
    setQcBusyId(null);
  };

  const keepImprovedQc = async () => {
    try {
      await qcApplyRevision(qcCompare.approval_id, qcCompare.revision_id);
      toast.success("Improved design applied — the original stays in revision history");
      setQcCompare(null);
      inv();
    } catch { toast.error("Could not apply the revision"); }
  };

  const runReadiness = async (item) => {
    setReadyBusyId(item.id);
    toast.loading("🚀 Final agency review in progress…", { id: "ready" });
    try {
      const { job_id } = await qcReadiness(item.id);
      const job = await pollImageJob(job_id);
      toast.dismiss("ready");
      if (job.status === "error") toast.error(job.error || "Readiness review failed");
      else setReadiness({ ...job.result, item });
      inv();
    } catch (e) { toast.dismiss("ready"); toast.error(e?.response?.data?.detail || "Readiness review failed"); }
    setReadyBusyId(null);
  };

  const publishAnyway = () => {
    const item = readiness?.item;
    setReadiness(null);
    if (!item) return;
    const isMetaPost = ["Facebook", "Instagram"].includes(item.platform) || item.type === "social" || item.type === "instagram_post";
    if (item.status === "approved" && isMetaPost) publishSocial.mutate(item.id);
    else if (item.status === "approved") move(item, "published", manualPub());
    else { move(item, "approved"); toast.success("Approved — publish from the card when ready"); }
  };

  const actions = (item) => {
    const isMetaPost = ["Facebook", "Instagram"].includes(item.platform)
      || (item.platform || "").toLowerCase().includes("facebook")
      || (item.platform || "").toLowerCase().includes("instagram")
      || item.type === "social" || item.type === "instagram_post";
    switch (item.status) {
      case "draft": return [{ l: "Submit", i: PaperPlaneTilt, fn: () => move(item, "pending"), cls: "text-amber-400" }];
      case "pending": return [
        { l: "Approve", i: CheckCircle, fn: () => move(item, "approved"), cls: "text-emerald-400" },
        { l: "Reject", i: XCircle, fn: () => move(item, "draft"), cls: "text-red-400" },
      ];
      case "approved": return [
        ...(item.type === "shopify_write"
          ? [{ l: "Apply to Shopify", i: Storefront, fn: () => applyShopify.mutate(item.id), cls: "brand-text" }]
          : isMetaPost
          ? [
            { l: "Schedule", i: CalendarPlus, fn: () => openSched(item), cls: "text-sky-400" },
            { l: "Publish Now", i: Rocket, fn: () => publishSocial.mutate(item.id), cls: "brand-text" },
          ]
          : [
            { l: "Schedule", i: CalendarPlus, fn: () => openSched(item), cls: "text-sky-400" },
            { l: "Publish", i: Rocket, fn: () => move(item, "published", manualPub()), cls: "brand-text" },
          ]),
      ];
      case "scheduled": return [
        { l: isMetaPost ? "Publish Now" : "Publish", i: Rocket,
          fn: () => isMetaPost ? publishSocial.mutate(item.id) : move(item, "published", manualPub()), cls: "brand-text" },
        { l: "Edit Schedule", i: CalendarPlus, fn: () => openSched(item), cls: "text-sky-400" },
        { l: "Unschedule", i: XCircle, fn: () => move(item, "approved", { scheduled_at: "" }), cls: "text-[#9a9a9a]" },
      ];
      case "failed": return [
        { l: "Retry Publish", i: Rocket, fn: () => publishSocial.mutate(item.id), cls: "brand-text" },
        { l: "Back to Approved", i: CheckCircle, fn: () => move(item, "approved"), cls: "text-emerald-400" },
      ];
      case "simulated": return [
        ...(isMetaPost ? [{ l: "Publish Live", i: Rocket, fn: () => publishSocial.mutate(item.id), cls: "brand-text" }] : []),
        { l: "Back to Approved", i: CheckCircle, fn: () => move(item, "approved"), cls: "text-emerald-400" },
      ];
      default: return [];
    }
  };

  const Card = ({ item }) => {
    const cnt = (item.comments || []).length;
    return (
      <div data-testid="approval-card" className={`card-matte p-3 ${selected.has(item.id) ? "ring-1 ring-[#6B3DFF]/60" : ""}`}>
        <div className="flex items-start gap-2 mb-1.5">
          <input type="checkbox" data-testid={`select-${item.id}`} checked={selected.has(item.id)}
            onChange={() => toggleSel(item.id)} className="mt-1 accent-[#6B3DFF] cursor-pointer" />
          <div className="flex-1 min-w-0">
            {item.meta?.video_path ? (
              <div className="relative mb-2" data-testid={`video-badge-${item.id}`}>
                <img src={`${BACKEND}${item.image_path}`} alt="" className="w-full h-24 object-cover rounded-md" />
                <span className="absolute bottom-1.5 left-1.5 text-[9px] font-bold uppercase tracking-wider bg-black/70 text-white px-1.5 py-0.5 rounded">▶ Video</span>
              </div>
            ) : item.image_url ? <img src={item.image_url} alt="" className="w-full h-24 object-cover rounded-md mb-2" />
              : item.image_path ? <img src={`${BACKEND}${item.image_path}`} alt="" className="w-full h-24 object-cover rounded-md mb-2" />
              : item.meta?.brand_check === "missing_product_photo" ? (
                <div data-testid={`brand-blocked-${item.id}`} className="w-full h-24 rounded-md mb-2 border border-dashed border-amber-500/30 bg-amber-500/[0.05] flex flex-col items-center justify-center text-[10px] text-amber-300 text-center px-2">
                  <Warning size={16} className="mb-1" /> Official product image required — upload or choose from Media Library.
                </div>
              ) : item.meta?.brand_check === "failed" ? (
                <div data-testid={`brand-failed-${item.id}`} className="w-full h-24 rounded-md mb-2 border border-dashed border-red-500/30 bg-red-500/[0.05] flex flex-col items-center justify-center text-[10px] text-red-300 text-center px-2">
                  <Warning size={16} className="mb-1" /> Brand check failed — image blocked. Regenerate with official brand assets.
                </div>
              ) : item.meta?.image_status === "generating" || item.meta?.image_status === "pending" ? (
                <div data-testid={`image-generating-${item.id}`} className="w-full h-24 rounded-md mb-2 border border-dashed border-[#6B3DFF]/25 bg-[#6B3DFF]/[0.04] flex flex-col items-center justify-center text-[10px] text-[#A56CFF] text-center px-2 ai-thinking">
                  Generating image… approval unlocks when it's ready
                </div>
              ) : item.meta?.image_status === "failed" ? (
                <div data-testid={`image-failed-${item.id}`} className="w-full h-24 rounded-md mb-2 border border-dashed border-red-500/25 bg-red-500/[0.05] flex flex-col items-center justify-center text-[10px] text-red-300 text-center px-2 overflow-hidden">
                  <ImageBroken size={16} className="mb-1 opacity-70" />
                  <span className="line-clamp-3">{item.meta?.image_error || "Image generation failed"}</span>
                </div>
              ) : null}
            {(item.image_url || item.image_path) && (item.meta?.brand_verified === true || item.meta?.brand_check === "official_product_photo") && (
              <div data-testid={`brand-verified-${item.id}`} className="flex items-center gap-1 mb-1.5 text-[9px] font-bold text-emerald-400">
                <CheckCircle size={11} weight="fill" /> BRAND VERIFIED{item.meta?.image_source ? ` · ${item.meta.image_source}` : ""}
              </div>
            )}
            {(item.image_url || item.image_path) && (
              ["client_uploaded", "product", "branded_graphic", "imported"].includes(item.meta?.image_source) ? (
                <div data-testid={`image-source-${item.id}`} className="flex items-center gap-1 mb-1.5 text-[9px] font-bold text-emerald-400 uppercase tracking-wide">
                  <CheckCircle size={11} weight="fill" /> Real photo · {String(item.meta.image_source).replace(/_/g, " ")}
                </div>
              ) : (
                <div data-testid={`image-source-${item.id}`} className="flex items-center gap-1 mb-1.5 text-[9px] font-bold text-amber-300 uppercase tracking-wide"
                  title="AI-generated image — it may not represent your actual completed work, product or property.">
                  <Warning size={11} weight="fill" /> AI image — may not show your actual work
                </div>
              )
            )}
            <button onClick={() => setDetailId(item.id)} data-testid={`open-detail-${item.id}`}
              className="font-semibold text-sm leading-snug text-left hover:text-[#A56CFF] block w-full">{item.title}</button>
            {item.meta?.marketing_score && (
              <button data-testid={`score-chip-${item.id}`} onClick={() => setScoreView({ title: item.title, score: item.meta.marketing_score })}
                className={`inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded border mt-1 ${gradeColor(item.meta.marketing_score.grade)}`}>
                Score {item.meta.marketing_score.grade} · {item.meta.marketing_score.overall}
              </button>
            )}
          </div>
          <button onClick={() => delM.mutate(item.id)} className="text-[#7a7a7a] hover:text-red-400 shrink-0"><Trash size={13} /></button>
        </div>
        <div className="text-[10px] uppercase tracking-wider text-[#7a7a7a]">{item.platform || item.type}{item.created_by ? ` · ${item.created_by}` : ""}</div>
        {(["Facebook", "Instagram"].includes(item.platform) || item.type === "social" || item.type === "instagram_post") && socialDest && (socialDest.pageName || socialDest.needsPage) && (
          socialDest.needsPage ? (
            <div className="flex items-center gap-1 text-[10px] text-amber-300 mt-1" data-testid={`card-dest-${item.id}`}><Warning size={11} weight="fill" /> No active Page selected</div>
          ) : (
            <div className="flex items-center gap-1.5 text-[10px] text-[#9a9a9a] mt-1" data-testid={`card-dest-${item.id}`}>
              {item.platform === "Instagram"
                ? <><InstagramLogo size={11} className="text-pink-400" /> {socialDest.igHandle ? `@${socialDest.igHandle}` : (socialDest.igId ? "IG linked" : socialDest.pageName)}</>
                : <><FacebookLogo size={11} className="text-sky-400" /> {socialDest.pageName}</>}
              {socialDest.testMode && <span className="text-amber-400">· test</span>}
            </div>
          )
        )}
        <div className="text-xs text-[#9a9a9a] mt-1.5 line-clamp-3 whitespace-pre-wrap">{item.body}</div>
        {item.hashtags && <div className="text-[11px] text-sky-400 mt-1 line-clamp-2">{item.hashtags}</div>}
        {item.status === "failed" && item.publish_result?.error && (
          <div className="text-[11px] mt-2 bg-red-500/10 border border-red-500/20 rounded px-2 py-1" data-testid={`fail-detail-${item.id}`}>
            {(item.publish_result.status === "unverified" || ["awaiting_verification", "verification_failed"].includes(item.publish_result.verification_status)) && (
              <div className="flex items-center gap-1 font-semibold text-amber-300 mb-0.5"><Warning size={12} weight="fill" />
                {item.publish_result.verification_status === "verification_failed" ? "Verification failed" : "Awaiting verification"} — not retried (duplicate risk)
              </div>
            )}
            <span className="text-red-400">{item.publish_result.error}</span>
            {/(token|expired|oauth|authoriz|session|reconnect|permission)/i.test(item.publish_result.error || "") && (
              <button data-testid={`reconnect-account-${item.id}`} onClick={() => navigate("/social")}
                className="block mt-1 text-[10px] font-semibold text-amber-300 hover:text-white underline underline-offset-2">
                Connection expired? Reconnect Account →
              </button>
            )}
          </div>
        )}
        {["published", "simulated"].includes(item.status) && (() => {
          const pr = item.publish_result || {};
          const sim = item.status === "simulated";
          const verified = pr.verification_status === "verified";
          const isIg = (item.platform || "").toLowerCase().includes("instagram");
          const isFb = (item.platform || "").toLowerCase().includes("facebook") || item.type === "social";
          const postId = item.platform_post_id || pr.post_id;
          const mock = String(postId || "").startsWith("mock_");
          const url = item.platform_post_url || pr.permalink ||
            (isFb && verified && postId && !mock ? `https://www.facebook.com/${postId}` : null);
          const stamp = fmtStamp(pr.at || item.published_at);
          return (
            <div className={`mt-2 rounded-lg px-2.5 py-2 space-y-1 border ${sim ? "bg-amber-500/[0.06] border-amber-500/15" : "bg-emerald-500/[0.06] border-emerald-500/15"}`} data-testid={`publish-audit-${item.id}`}>
              <div className={`flex items-center gap-1.5 text-[11px] font-semibold ${sim ? "text-amber-300" : "text-emerald-400"}`} data-testid={`published-at-${item.id}`}>
                {sim ? <Warning size={13} weight="fill" /> : <CheckCircle size={13} weight="fill" />}
                {sim ? "Simulated — NOT published" : "Published"}{stamp ? `: ${stamp}` : ""}
                {verified && (
                  <span data-testid={`verified-badge-${item.id}`} className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">VERIFIED LIVE</span>
                )}
              </div>
              {sim && (
                <div className="text-[10px] text-amber-300/80">{pr.test_mode ? `Test mode — nothing was sent to ${isIg ? "Instagram" : "Facebook"}.` : "No delivery provider connected — this item was simulated, not sent."}</div>
              )}
              {pr.verification_status === "manual" && (
                <div className="text-[10px] text-[#9a9a9a]">Manually marked as published — not sent via Vozelto.</div>
              )}
              <div className="flex items-center gap-1.5 text-[11px] text-[#cfcfcf]" data-testid={`published-by-${item.id}`}>
                <UserCircle size={13} className="text-[#9a9a9a]" /> {sim ? "Run by" : "Published by"}: <span className="font-semibold">{publishedBy(item)}</span>
              </div>
              {pr.page_name && (
                <div className="flex items-center gap-1.5 text-[11px] text-[#cfcfcf]" data-testid={`published-page-${item.id}`}>
                  {isIg ? <InstagramLogo size={13} className="text-pink-400" /> : <FacebookLogo size={13} className="text-sky-400" />} Page: {pr.page_name}
                </div>
              )}
              <div className="flex items-center gap-2 flex-wrap pt-0.5">
                {url && (
                  <>
                    <a data-testid={`view-post-${item.id}`} href={url} target="_blank" rel="noopener noreferrer"
                      className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-1 rounded-md ${isIg ? "bg-pink-500/10 text-pink-300 hover:bg-pink-500/20" : "bg-sky-500/10 text-sky-300 hover:bg-sky-500/20"}`}>
                      {isIg ? <InstagramLogo size={13} weight="fill" /> : <FacebookLogo size={13} weight="fill" />} View on {isIg ? "Instagram" : "Facebook"} <ArrowSquareOut size={11} weight="bold" />
                    </a>
                    <button data-testid={`copy-link-${item.id}`}
                      onClick={() => copyText(url, "Post link copied")}
                      className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-1 rounded-md bg-white/5 text-[#cfcfcf] hover:bg-white/10">
                      <Copy size={12} /> Copy Link
                    </button>
                  </>
                )}
              </div>
              {postId && (
                <button data-testid={`post-id-${item.id}`} title="Click to copy the post ID"
                  onClick={() => copyText(postId, "Post ID copied")}
                  className="flex items-center gap-1.5 text-[10px] text-[#9a9a9a] hover:text-white">
                  <Info size={12} /> {isIg ? "Instagram Media" : isFb ? "Facebook Post" : "Post"} ID: <span className="font-mono">{postId}</span> <Copy size={10} />
                </button>
              )}
            </div>
          );
        })()}
        {item.campaign_id && campaignName[item.campaign_id] && (
          <div className="flex items-center gap-1 text-[10px] text-[#A56CFF] mt-2"><FolderSimple size={12} /> {campaignName[item.campaign_id]}</div>
        )}
        <div className="flex items-center gap-3 mt-2 text-[11px] text-[#7a7a7a]">
          {item.scheduled_at && (() => {
            const d = publishDate(item); const f = d ? fmtPublish(d) : null;
            return <span data-testid={`sched-time-${item.id}`} className="flex items-center gap-1 text-sky-400">
              <Clock size={12} /> {f ? `${f.date} · ${f.time} ${f.tz}` : item.scheduled_at}</span>;
          })()}
          {cnt > 0 && <span className="flex items-center gap-1"><ChatCircleDots size={12} /> {cnt}</span>}
          {item.applied_status === "simulated" && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300" title="Applied in simulation — Shopify not connected">SIMULATED</span>}
        </div>
        {["draft", "pending", "approved", "scheduled"].includes(item.status) && (
          <div className="flex flex-wrap items-center gap-1.5 mt-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid={`ai-assistant-${item.id}`} disabled={!!qcBusyId || !!readyBusyId || !!rewriteBusyId || !!regenPostBusyId}
                  className="flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-md bg-[#6B3DFF]/10 hover:bg-[#6B3DFF]/20 border border-[#6B3DFF]/25 text-[#C39BFF] disabled:opacity-50">
                  <Sparkle size={12} weight="fill" className={qcBusyId === item.id || rewriteBusyId === item.id ? "ai-thinking" : ""} />
                  {qcBusyId === item.id ? "Second pass…" : rewriteBusyId === item.id ? "Rewriting…" : "AI Assistant"}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="bg-[#14141F] border-white/10 text-white max-h-80 overflow-y-auto" data-testid={`ai-assistant-menu-${item.id}`}>
                <DropdownMenuItem data-testid={`ai-score-${item.id}`} onClick={() => runScore(item)}
                  disabled={scoreBusyId === item.id}
                  className="text-sky-300 focus:bg-sky-500/15 focus:text-white cursor-pointer text-xs">
                  <RocketLaunch size={13} weight="fill" className="mr-1.5" /> {scoreBusyId === item.id ? "Scoring…" : "Marketing Score"}
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-white/10" />
                {item.image_path && (
                  <>
                    <DropdownMenuItem data-testid={`ai-improve-design-${item.id}`} onClick={() => runQcImprove(item)}
                      className="text-[#C39BFF] focus:bg-[#6B3DFF]/15 focus:text-white cursor-pointer text-xs">
                      <Sparkle size={13} weight="fill" className="mr-1.5" /> Improve Design
                    </DropdownMenuItem>
                    <DropdownMenuItem data-testid={`ai-regen-image-${item.id}`} onClick={() => runRegenImageOnly(item)}
                      className="focus:bg-[#6B3DFF]/15 focus:text-white cursor-pointer text-xs">
                      <ArrowsClockwise size={13} className="mr-1.5" /> Regenerate Image Only
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuItem data-testid={`use-my-photo-${item.id}`} onClick={() => setMediaPickFor(item)}
                  className="text-emerald-300 focus:bg-emerald-500/15 focus:text-white cursor-pointer text-xs">
                  <ImageSquare size={13} className="mr-1.5" /> Use My Photo (Media Library)
                </DropdownMenuItem>
                {(item.image_url || item.image_path) && (
                  <DropdownMenuItem data-testid={`remove-image-${item.id}`}
                    onClick={async () => { try { await mediaAttach(item.id, null); toast.success("Image removed — post will publish without an image"); inv(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } }}
                    className="focus:bg-white/10 cursor-pointer text-xs">
                    <XCircle size={13} className="mr-1.5" /> Post Without an Image
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem data-testid={`ai-regen-caption-${item.id}`} onClick={() => runRegenCaptionOnly(item)}
                  className="focus:bg-[#6B3DFF]/15 focus:text-white cursor-pointer text-xs">
                  <PencilSimple size={13} className="mr-1.5" /> Regenerate Caption Only
                </DropdownMenuItem>
                {isMetaItem(item) && !item.meta?.video && (
                  <DropdownMenuItem data-testid={`ai-video-from-post-${item.id}`} onClick={() => runVideoFromPost(item)}
                    className="text-[#C39BFF] focus:bg-[#6B3DFF]/15 focus:text-white cursor-pointer text-xs">
                    <ArrowsClockwise size={13} className="mr-1.5" /> Generate Promo Video
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator className="bg-white/10" />
                {REWRITE_OPTIONS.map((opt) => (
                  <DropdownMenuItem key={opt.l} data-testid={`ai-rewrite-${opt.l.toLowerCase().replace(/\s+/g, "-")}-${item.id}`}
                    onClick={() => runRewrite(item, opt)}
                    className="focus:bg-[#6B3DFF]/15 focus:text-white cursor-pointer text-xs">
                    {opt.l}
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator className="bg-white/10" />
                {TRANSLATE_OPTIONS.map((opt) => (
                  <DropdownMenuItem key={opt.l} data-testid={`ai-rewrite-${opt.l.toLowerCase().replace(/\s+/g, "-")}-${item.id}`}
                    onClick={() => runRewrite(item, opt)}
                    className="focus:bg-[#6B3DFF]/15 focus:text-white cursor-pointer text-xs">
                    {opt.l}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <button data-testid={`regen-post-${item.id}`} disabled={!!qcBusyId || !!readyBusyId || !!rewriteBusyId || !!regenPostBusyId}
              onClick={() => runRegenPost(item)}
              className="flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-md bg-white/5 hover:bg-white/10 border border-white/15 text-[#cfcfcf] disabled:opacity-50">
              <ArrowsClockwise size={12} weight="bold" className={regenPostBusyId === item.id ? "ai-thinking" : ""} />
              {regenPostBusyId === item.id ? "Regenerating…" : "Regenerate Post"}
            </button>
            {item.image_path && (
              <button data-testid={`qc-readiness-${item.id}`} disabled={!!qcBusyId || !!readyBusyId || !!rewriteBusyId || !!regenPostBusyId} onClick={() => runReadiness(item)}
                className="flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-md bg-sky-500/10 hover:bg-sky-500/20 border border-sky-500/25 text-sky-300 disabled:opacity-50">
                <RocketLaunch size={12} weight="fill" className={readyBusyId === item.id ? "ai-thinking" : ""} />
                {readyBusyId === item.id ? "Reviewing…" : "Campaign Readiness"}
              </button>
            )}
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2 mt-1.5">
          {item.score?.overall != null && (
            <span data-testid={`qc-score-${item.id}`} className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${item.score.overall >= 85 ? "bg-emerald-500/15 text-emerald-400" : item.score.overall >= 70 ? "bg-amber-500/15 text-amber-300" : "bg-red-500/15 text-red-400"}`}>
              AI {item.score.overall}
            </span>
          )}
          {item.readiness?.overall != null && (
            <span data-testid={`readiness-badge-${item.id}`} className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${item.readiness.overall >= 85 ? "bg-emerald-500/15 text-emerald-400" : "bg-sky-500/15 text-sky-300"}`}>
              🚀 Ready {item.readiness.overall}
            </span>
          )}
          {(item.revisions || []).length > 0 && (
            <button data-testid={`qc-history-${item.id}`} onClick={() => setHistoryItem(item)}
              className="flex items-center gap-1 text-[10px] text-[#9a9a9a] hover:text-white">
              <ClockCounterClockwise size={11} /> {(item.revisions || []).length} revision pass{(item.revisions || []).length > 1 ? "es" : ""}
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-1.5 mt-2.5">
          {actions(item).map((a) => (
            <button key={a.l} data-testid={`action-${a.l.toLowerCase().replace(/\s+/g, "-")}`} onClick={a.fn}
              className={`flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-md bg-white/5 hover:bg-white/10 ${a.cls}`}>
              <a.i size={13} weight="bold" /> {a.l}
            </button>
          ))}
        </div>
      </div>
    );
  };

  const Board = ({ pool }) => (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-6 gap-4">
      {COLUMNS.map((col) => {
        const list = pool.filter((i) => i.status === col.key);
        return (
          <div key={col.key} data-testid={`column-${col.key}`} className="bg-[#10101A] border border-white/8 rounded-xl p-3 min-h-[160px]">
            <div className="flex items-center justify-between mb-3 px-1">
              <span className={`text-xs font-bold uppercase tracking-wider ${col.color}`}>{col.label}</span>
              <span className="text-xs text-[#5a5a5a]">{list.length}</span>
            </div>
            <div className="space-y-2.5">
              {list.map((item) => <Card key={item.id} item={item} />)}
              {list.length === 0 && <div className="text-xs text-[#5a5a5a] text-center py-6">Empty</div>}
            </div>
          </div>
        );
      })}
    </div>
  );

  // group items by campaign (null campaign → "Standalone")
  const groups = useMemo(() => {
    const m = new Map();
    filteredItems.forEach((i) => {
      const k = i.campaign_id || "__none__";
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(i);
    });
    return [...m.entries()];
  }, [filteredItems]);

  return (
    <PageContainer>
      <PageHeader eyebrow="Workflow" title="Approval Queue"
        subtitle="Every AI-generated post, email, SMS, blog and ad flows through here. Nothing publishes until you approve it — you stay in full control." />

      {socialDest && (
        <div data-testid="publishing-destination-banner" className="mb-4">
          {socialDest.needsPage ? (
            <div className="flex items-center gap-2.5 rounded-xl border border-amber-500/30 bg-amber-500/[0.07] px-4 py-2.5">
              <Warning size={17} className="text-amber-400 shrink-0" weight="fill" />
              <span className="text-sm text-amber-200">No active Facebook Page selected for this workspace — social posts can't be published safely.</span>
              <button data-testid="banner-choose-page" onClick={() => navigate("/social")} className="ml-auto text-xs font-semibold text-amber-300 hover:text-white underline underline-offset-2 shrink-0">Choose a Page →</button>
            </div>
          ) : socialDest.testMode ? (
            <div className="flex items-center gap-2.5 rounded-xl border border-amber-500/25 bg-amber-500/[0.06] px-4 py-2.5">
              <PlugsConnected size={17} className="text-amber-400 shrink-0" weight="fill" />
              <span className="text-sm text-amber-200/90"><b>Test Mode</b> — social posts are simulated, not published live.</span>
              <button data-testid="banner-go-live" onClick={() => navigate("/social")} className="ml-auto text-xs font-semibold text-amber-300 hover:text-white underline underline-offset-2 shrink-0">Connect to go live →</button>
            </div>
          ) : socialDest.pageName ? (
            <div className="rounded-xl border border-emerald-500/25 bg-emerald-500/[0.06] px-4 py-2.5">
              <div className="flex items-center gap-2.5 flex-wrap">
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/25 shrink-0">● LIVE</span>
                {activeClient?.name && (
                  <span className="text-sm text-[#9a9a9a]">Workspace <span className="text-white font-semibold">{activeClient.name}</span></span>
                )}
                <ArrowRight size={14} className="text-[#5a5a5a] shrink-0" />
                <span className="text-sm text-[#cfcfcf] flex items-center gap-1.5 flex-wrap">
                  publishes to
                  <span className="inline-flex items-center gap-1 text-white font-semibold"><FacebookLogo size={15} className="text-sky-400" /> {socialDest.pageName}</span>
                  {socialDest.igId && (
                    <span className="inline-flex items-center gap-1 text-white font-semibold"><InstagramLogo size={15} className="text-pink-400" /> {socialDest.igHandle ? `@${socialDest.igHandle}` : "Instagram linked"}</span>
                  )}
                </span>
                <button data-testid="banner-change-page" onClick={() => navigate("/social")} className="ml-auto text-xs font-semibold text-[#9a9a9a] hover:text-white underline underline-offset-2 shrink-0">Change Page</button>
              </div>
              {activeClient?.name && socialDest.pageName &&
                activeClient.name.trim().toLowerCase() !== socialDest.pageName.trim().toLowerCase() && (
                <div data-testid="page-mismatch-note" className="flex items-start gap-1.5 mt-2 pt-2 border-t border-white/8 text-[11px] text-amber-200/80">
                  <Info size={13} className="text-amber-300/90 shrink-0 mt-0.5" weight="fill" />
                  <span>Heads up: this workspace is linked to the Meta Page <b className="text-amber-100">“{socialDest.pageName}”</b>, whose name differs from the workspace. Posts approved here publish to that Page — this is expected when you manage multiple brands/pages. Use <b>Change Page</b> to switch.</span>
                </div>
              )}
            </div>
          ) : null}
        </div>
      )}

      {/* Campaign Status Dashboard + Publishing Timeline */}
      <div className="grid lg:grid-cols-2 gap-3 mb-4">
        <div data-testid="campaign-status-dashboard" className="card-matte px-4 py-3">
          <div className="text-[10px] uppercase tracking-wider text-[#7a7a7a] mb-2">Campaign Status</div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
            {COLUMNS.map((c) => (
              <button key={c.key} data-testid={`status-count-${c.key}`} onClick={() => setStatusFilter(statusFilter === c.key ? "all" : c.key)}
                className={`flex items-center gap-1.5 text-xs ${statusFilter === c.key ? "text-white font-semibold" : "text-[#9a9a9a] hover:text-white"}`}>
                <span className={`h-2 w-2 rounded-full ${c.key === "pending" ? "bg-amber-400" : c.key === "approved" ? "bg-emerald-400" : c.key === "scheduled" ? "bg-sky-400" : c.key === "published" ? "bg-[#6B3DFF]" : c.key === "failed" ? "bg-red-400" : c.key === "simulated" ? "bg-amber-400/60" : "bg-[#6a6a6a]"}`} />
                {c.label}: <span className="font-bold text-white">{statusCounts[c.key] || 0}</span>
              </button>
            ))}
          </div>
        </div>
        <div data-testid="publishing-timeline" className="card-matte px-4 py-3">
          <div className="text-[10px] uppercase tracking-wider text-[#7a7a7a] mb-2">Publishing Next</div>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5">
            {[["Next Post", nextScheduled.post], ["Next Email", nextScheduled.email], ["Next SMS", nextScheduled.sms], ["Next Blog", nextScheduled.blog]]
              .filter(([, x]) => x).map(([label, x]) => (
                <div key={label} data-testid={`next-${label.toLowerCase().replace(/\s+/g, "-")}`} className="flex items-center gap-2 text-xs">
                  <Clock size={13} className="text-sky-400" />
                  <span className="text-[#9a9a9a]">{label}</span>
                  <span className="text-white font-semibold">{x.i.platform || platformOf(x.i)}</span>
                  <span className="text-sky-300">{relDay(x.d)} · {x.d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}</span>
                </div>
              ))}
            {!nextScheduled.post && !nextScheduled.email && !nextScheduled.sms && !nextScheduled.blog && (
              <span className="text-xs text-[#6a6a6a]">Nothing scheduled yet — approve items to fill the calendar.</span>
            )}
          </div>
        </div>
      </div>

      {/* Campaign Completion summary */}
      {completionSummary && !completionDismissed && (
        <div data-testid="campaign-complete-banner" className="card-matte brand-glow px-4 py-3 mb-4 flex flex-wrap items-center gap-x-5 gap-y-1.5">
          <span className="flex items-center gap-1.5 text-sm font-semibold text-emerald-400"><CheckCircle size={16} weight="fill" /> Campaign Complete</span>
          <span className="text-xs text-[#cfcfcf]">{completionSummary.campaign?.name}</span>
          <span className="text-xs text-white font-semibold">{completionSummary.published} published</span>
          <span className={`text-xs font-semibold ${completionSummary.failed ? "text-red-400" : "text-[#9a9a9a]"}`}>{completionSummary.failed} failed</span>
          <span className="text-xs text-[#9a9a9a]">Est. reach <b className="text-white">{completionSummary.estimates.reach.toLocaleString()}</b> · engagement <b className="text-white">{completionSummary.estimates.engagement.toLocaleString()}</b> · clicks <b className="text-white">{completionSummary.estimates.clicks.toLocaleString()}</b> <span className="text-[10px]">(estimates)</span></span>
          <button data-testid="dismiss-completion" onClick={() => setCompletionDismissed(true)} className="ml-auto text-[#7a7a7a] hover:text-white"><XCircle size={16} /></button>
        </div>
      )}

      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-2 flex-wrap">
          <button data-testid="toggle-group" onClick={() => setGroupByCampaign((v) => !v)}
            className={`flex items-center gap-2 text-sm px-3 py-1.5 rounded-lg border ${groupByCampaign ? "border-[#6B3DFF]/40 bg-[#6B3DFF]/10 text-white" : "border-white/15 text-[#9a9a9a] hover:text-white"}`}>
            <FolderSimple size={15} /> {groupByCampaign ? "Grouped by campaign" : "Group by campaign"}
          </button>
          {/* Platform filter */}
          <div className="flex items-center gap-1 text-xs text-[#7a7a7a]"><Funnel size={13} /></div>
          {PLATFORM_FILTERS.map((p) => (
            <button key={p} data-testid={`filter-platform-${p.toLowerCase()}`} onClick={() => setPlatformFilter(p)}
              className={`flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg border ${platformFilter === p ? "border-[#6B3DFF]/40 bg-[#6B3DFF]/10 text-white" : "border-white/12 text-[#9a9a9a] hover:text-white"}`}>
              {p === "Facebook" && <FacebookLogo size={13} className="text-sky-400" />}
              {p === "Instagram" && <InstagramLogo size={13} className="text-pink-400" />}
              {p === "all" ? "All platforms" : p}
              {["LinkedIn", "TikTok"].includes(p) && <span className="text-[9px] text-[#6a6a6a]">soon</span>}
            </button>
          ))}
          {/* Status quick filters */}
          <div className="w-full flex items-center gap-1.5 flex-wrap">
            {[{ key: "all", label: "All" }, ...COLUMNS].map((c) => (
              <button key={c.key} data-testid={`filter-status-${c.key}`} onClick={() => setStatusFilter(c.key)}
                className={`text-xs px-2.5 py-1 rounded-full border ${statusFilter === c.key ? "border-[#6B3DFF]/40 bg-[#6B3DFF]/10 text-white" : "border-white/12 text-[#9a9a9a] hover:text-white"}`}>
                {c.label}{c.key !== "all" ? ` (${statusCounts[c.key] || 0})` : ""}
              </button>
            ))}
          </div>
          {employees.length > 0 && (
            <select data-testid="filter-employee" value={employeeFilter} onChange={(e) => setEmployeeFilter(e.target.value)}
              className="bg-[#10101A] border border-white/12 rounded-lg px-2.5 py-1.5 text-xs text-[#cfcfcf]">
              <option value="all">All AI employees</option>
              {employees.map((emp) => <option key={emp} value={emp}>{emp}</option>)}
            </select>
          )}
          {objectives.length > 0 && (
            <select data-testid="filter-objective" value={objectiveFilter} onChange={(e) => setObjectiveFilter(e.target.value)}
              className="bg-[#10101A] border border-white/12 rounded-lg px-2.5 py-1.5 text-xs text-[#cfcfcf] capitalize">
              <option value="all">All objectives</option>
              {objectives.map((o) => <option key={o} value={o}>{o.replace(/_/g, " ")}</option>)}
            </select>
          )}
          <input data-testid="queue-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search content…"
            className="bg-[#10101A] border border-white/12 rounded-lg px-3 py-1.5 text-xs text-white w-40 placeholder:text-[#5a5a5a]" />
          {(platformFilter !== "all" || statusFilter !== "all" || employeeFilter !== "all" || objectiveFilter !== "all" || search) && (
            <button data-testid="filter-clear" onClick={() => { setPlatformFilter("all"); setStatusFilter("all"); setEmployeeFilter("all"); setObjectiveFilter("all"); setSearch(""); }}
              className="text-xs text-[#7a7a7a] hover:text-white underline underline-offset-2">Clear filters</button>
          )}
          <button data-testid="queue-select-all" onClick={() => setSelected(new Set(filteredItems.map((i) => i.id)))}
            className="text-xs px-2.5 py-1.5 rounded-lg border border-white/12 text-[#9a9a9a] hover:text-white">
            Select all ({filteredItems.length})
          </button>
          {filteredItems.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="bulk-actions-menu" className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10 font-semibold">
                  <ListChecks size={14} /> Bulk Actions
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="bg-[#14141F] border-white/10 text-white max-h-96 overflow-y-auto" data-testid="bulk-actions-content">
                <DropdownMenuItem data-testid="bulk-approve-all-pending" onClick={() => askApproveAll(null, "All Pending")}
                  className="focus:bg-emerald-500/15 focus:text-white cursor-pointer text-xs text-emerald-300">
                  <CheckCircle size={13} className="mr-1.5" /> Approve All Pending ({filteredItems.filter((i) => i.status === "pending").length})
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-white/10" />
                {[["Facebook", "Facebook Posts"], ["Instagram", "Instagram Posts"], ["LinkedIn", "LinkedIn Posts"], ["TikTok", "TikTok Posts"], ["Email", "Email Campaigns"], ["SMS", "SMS Campaigns"], ["Blog", "Blog Articles"]].map(([p, label]) => {
                  const n = filteredItems.filter((i) => i.status === "pending" && platformOf(i) === p).length;
                  const future = ["LinkedIn", "TikTok"].includes(p) && n === 0;
                  return (
                    <DropdownMenuItem key={p} data-testid={`bulk-approve-${p.toLowerCase()}`} disabled={future}
                      onClick={() => askApproveAll((i) => platformOf(i) === p, `All ${label}`)}
                      className="focus:bg-[#6B3DFF]/15 focus:text-white cursor-pointer text-xs">
                      Approve All {label} ({n}){future ? " — soon" : ""}
                    </DropdownMenuItem>
                  );
                })}
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem data-testid="bulk-approve-selected" onClick={askApproveSelected} className="focus:bg-emerald-500/15 focus:text-white cursor-pointer text-xs">
                  Approve Selected ({selected.size})
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="bulk-reject-selected" onClick={askRejectSelected} className="focus:bg-red-500/15 focus:text-white cursor-pointer text-xs text-red-300">
                  Reject Selected ({selected.size})
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="bulk-reschedule-selected" onClick={() => (selected.size ? setReschedOpen(true) : toast.info("Select items first (checkboxes)"))}
                  className="focus:bg-sky-500/15 focus:text-white cursor-pointer text-xs text-sky-300">
                  Reschedule Selected ({selected.size})
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {selected.size > 0 && (
            <div data-testid="bulk-bar" className="flex items-center gap-2 flex-wrap bg-[#14141F] border border-[#6B3DFF]/30 rounded-lg px-3 py-2">
              <span className="text-sm text-[#cfcfcf] flex items-center gap-1.5"><ListChecks size={15} className="brand-text" /> {selected.size} selected</span>
              <Button data-testid="bulk-approve" size="sm" onClick={() => bulk("approved")} className="bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300 h-7"><CheckCircle size={14} className="mr-1" />Approve</Button>
              <Button data-testid="bulk-reject" size="sm" onClick={() => bulk("draft")} className="bg-red-500/15 hover:bg-red-500/25 text-red-300 h-7"><XCircle size={14} className="mr-1" />Reject</Button>
              <Button data-testid="bulk-publish" size="sm" onClick={bulkPublish} className="bg-[#6B3DFF]/15 hover:bg-[#6B3DFF]/25 text-[#A56CFF] h-7"><Rocket size={14} className="mr-1" />Publish</Button>
              <Button data-testid="bulk-delete" size="sm" onClick={bulkDelete} className="bg-white/5 hover:bg-white/10 text-[#9a9a9a] h-7"><Trash size={14} className="mr-1" />Delete</Button>
              <Button data-testid="bulk-clear" size="sm" variant="ghost" onClick={clearSel} className="text-[#7a7a7a] h-7">Clear</Button>
            </div>
          )}
          <Button data-testid="cleanup-open-btn" size="sm" variant="outline" onClick={() => setCleanupOpen(true)}
            className="border-red-500/30 bg-transparent text-red-300 hover:bg-red-500/10 h-8"><Broom size={15} className="mr-1.5" />Clean up test content</Button>
        </div>
      </div>

      {!groupByCampaign ? (
        <Board pool={filteredItems} />
      ) : (
        <div className="space-y-8">
          {groups.map(([cid, pool]) => (
            <div key={cid} data-testid={`group-${cid}`}>
              <div className="flex items-center gap-2 mb-3">
                <FolderSimple size={16} className="brand-text" weight="fill" />
                <span className="font-head font-semibold text-base">{cid === "__none__" ? "Standalone items" : (campaignName[cid] || "Campaign")}</span>
                <span className="text-xs text-[#5a5a5a]">{pool.length} items</span>
              </div>
              <Board pool={pool} />
            </div>
          ))}
        </div>
      )}

      <MarketingScoreDialog open={!!scoreView} onClose={() => setScoreView(null)}
        title={scoreView?.title} score={scoreView?.score} />

      <MediaPicker clientId={activeId} open={!!mediaPickFor} onClose={() => setMediaPickFor(null)}
        title={`Choose a photo for “${mediaPickFor?.title || ""}”`}
        onSelect={async (asset) => {
          try {
            await mediaAttach(mediaPickFor.id, asset.id);
            toast.success(`Photo attached: ${asset.title}`);
            setMediaPickFor(null); inv();
          } catch (e) { toast.error(e?.response?.data?.detail || "Could not attach photo"); }
        }} />

      {/* Bulk confirmation dialog */}
      <Dialog open={!!confirmAction} onOpenChange={(o) => !o && setConfirmAction(null)}>
        <DialogContent className="bg-[#14141F] border-white/10 text-white" data-testid="bulk-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-head">{confirmAction?.title}</DialogTitle>
            <DialogDescription className="text-[#b5b5b5]">{confirmAction?.message}</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button data-testid="bulk-confirm-cancel" variant="ghost" onClick={() => setConfirmAction(null)} className="text-[#9a9a9a]">Cancel</Button>
            <Button data-testid="bulk-confirm-ok" onClick={() => { const fn = confirmAction?.fn; setConfirmAction(null); fn && fn(); }}
              className={confirmAction?.cta === "Reject" ? "bg-red-500/20 hover:bg-red-500/30 text-red-200 border border-red-500/30" : "bg-[#6B3DFF] hover:bg-[#A56CFF] text-white"}>
              {confirmAction?.cta || "Confirm"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk reschedule dialog */}
      <Dialog open={reschedOpen} onOpenChange={setReschedOpen}>
        <DialogContent className="bg-[#14141F] border-white/10 text-white" data-testid="bulk-reschedule-dialog">
          <DialogHeader>
            <DialogTitle className="font-head">Reschedule {selected.size} selected item(s)</DialogTitle>
            <DialogDescription className="text-[#7a7a7a]">Pick the new publish date &amp; time in your local timezone ({TZ_LABEL}).</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="label-caps">New date</Label><Input data-testid="resched-date" type="date" value={reschedDate} onChange={(e) => setReschedDate(e.target.value)} className="bg-[#10101A] border-white/10 mt-1" /></div>
            <div><Label className="label-caps">Time</Label><Input data-testid="resched-time" type="time" value={reschedTime} onChange={(e) => setReschedTime(e.target.value)} className="bg-[#10101A] border-white/10 mt-1" /></div>
          </div>
          <DialogFooter><Button data-testid="resched-confirm" onClick={applyReschedule} className="bg-[#6B3DFF] hover:bg-[#A56CFF] text-white">Reschedule</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Schedule dialog */}
      <Dialog open={!!schedFor} onOpenChange={(o) => !o && setSchedFor(null)}>
        <DialogContent className="bg-[#14141F] border-white/10 text-white">
          <DialogHeader><DialogTitle className="font-head">{schedFor?.status === "scheduled" ? "Edit schedule" : "Schedule"} "{schedFor?.title}"</DialogTitle>
            <DialogDescription className="text-[#7a7a7a]">Pick the publish date &amp; time in your local timezone ({TZ_LABEL}). Auto Pilot publishes it at exactly this time — no re-approval needed.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="label-caps">Publish date</Label><Input data-testid="schedule-date" type="date" value={schedDate} onChange={(e) => setSchedDate(e.target.value)} className="bg-[#10101A] border-white/10 mt-1" /></div>
            <div><Label className="label-caps">Time</Label><Input data-testid="schedule-time" type="time" value={schedTime} onChange={(e) => setSchedTime(e.target.value)} className="bg-[#10101A] border-white/10 mt-1" /></div>
          </div>
          <div className="flex gap-2">
            <button data-testid="schedule-quick-now" onClick={() => quickSched(0)} className="text-[11px] font-semibold px-2.5 py-1.5 rounded-md bg-white/5 hover:bg-white/10 border border-white/10 text-[#cfcfcf]">Now</button>
            <button data-testid="schedule-quick-15" onClick={() => quickSched(15)} className="text-[11px] font-semibold px-2.5 py-1.5 rounded-md bg-white/5 hover:bg-white/10 border border-white/10 text-[#cfcfcf]">In 15 minutes</button>
          </div>
          <DialogFooter><Button data-testid="confirm-schedule-btn" onClick={confirmSchedule} className="bg-[#6B3DFF] hover:bg-[#A56CFF] text-white">{schedFor?.status === "scheduled" ? "Update Schedule" : "Schedule"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cleanup dialog */}
      <Dialog open={cleanupOpen} onOpenChange={setCleanupOpen}>
        <DialogContent className="bg-[#14141F] border-white/10 text-white" data-testid="cleanup-dialog">
          <DialogHeader><DialogTitle className="font-head flex items-center gap-2"><Broom size={18} className="text-red-300" /> Clean up test content</DialogTitle>
            <DialogDescription className="text-[#7a7a7a]">Permanently delete approval items in the selected columns for <b className="text-[#cfcfcf]">{activeClient?.name || "this workspace"}</b>. Published history is kept unless you tick it below.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {COLUMNS.map((c) => {
              const count = items.filter((i) => i.status === c.key).length;
              const isPub = c.key === "published";
              return (
                <label key={c.key} data-testid={`cleanup-row-${c.key}`}
                  className={`flex items-center justify-between rounded-lg border px-3 py-2 cursor-pointer ${cleanupSel[c.key] ? "border-red-500/30 bg-red-500/[0.06]" : "border-white/8"}`}>
                  <span className="flex items-center gap-2.5">
                    <Checkbox data-testid={`cleanup-check-${c.key}`} checked={!!cleanupSel[c.key]}
                      onCheckedChange={(v) => setCleanupSel((s) => ({ ...s, [c.key]: !!v }))} />
                    <span className={`text-sm ${c.color}`}>{c.label}</span>
                    {isPub && <span className="text-[10px] text-amber-300/80">(history — off by default)</span>}
                  </span>
                  <span className="text-xs text-[#7a7a7a]">{count} item{count === 1 ? "" : "s"}</span>
                </label>
              );
            })}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setCleanupOpen(false)} className="text-[#9a9a9a]">Cancel</Button>
            <Button data-testid="cleanup-confirm-btn" onClick={() => cleanup.mutate()} disabled={cleanup.isPending || !Object.values(cleanupSel).some(Boolean)}
              className="bg-red-500/20 hover:bg-red-500/30 text-red-200 border border-red-500/30">
              <Trash size={15} className="mr-1.5" />{cleanup.isPending ? "Clearing…" : "Delete selected"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Detail dialog: body + comments + version history */}
      <Dialog open={!!detailId} onOpenChange={(o) => !o && setDetailId(null)}>
        <DialogContent className="bg-[#14141F] border-white/10 text-white max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-head pr-6">{detail?.title}</DialogTitle>
            <DialogDescription className="text-[#7a7a7a]">Review details, leave internal notes for your team, and see the full status history.</DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="space-y-5">
              <div className="text-[10px] uppercase tracking-wider text-[#7a7a7a]">{detail.platform || detail.type} · {STATUS_LABEL[detail.status] || detail.status}</div>
              {detail.meta?.video_path ? (
                <div className="space-y-2">
                  <video data-testid="detail-video-player" controls preload="metadata" playsInline
                    poster={detail.image_path ? `${BACKEND}${detail.image_path}` : undefined}
                    src={`${BACKEND}${detail.meta.video_path}`} className="w-full rounded-lg bg-black max-h-[380px]" />
                  <a data-testid="detail-video-download" href={`${BACKEND}${detail.meta.video_path}`} download
                    className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1.5 rounded-md bg-[#6B3DFF]/15 text-[#A56CFF] hover:bg-[#6B3DFF]/25">
                    <ArrowSquareOut size={13} /> Download MP4
                  </a>
                </div>
              ) : detail.image_path && <img src={`${BACKEND}${detail.image_path}`} alt="" className="w-full rounded-lg" />}

              {/* Body — preview / edit / regenerate */}
              <div data-testid="detail-body-section">
                <div className="flex items-center justify-between mb-2">
                  <div className="label-caps">{(detail.deliverable_type || detail.type) === "image" ? "Image" : "Content"}</div>
                  <div className="flex items-center gap-1.5">
                    {(detail.deliverable_type || detail.type) !== "image" && (
                      editing ? (
                        <>
                          <Button data-testid="save-edit-btn" size="sm" onClick={() => saveEdit.mutate({ id: detail.id, item: detail })} disabled={saveEdit.isPending}
                            className="h-7 bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300"><FloppyDisk size={13} className="mr-1" />Save</Button>
                          <Button data-testid="cancel-edit-btn" size="sm" variant="ghost" onClick={() => { setEditing(false); setEditBody(detail.body || ""); }} className="h-7 text-[#9a9a9a]">Cancel</Button>
                        </>
                      ) : (
                        <Button data-testid="edit-btn" size="sm" variant="ghost" onClick={() => { setEditBody(detail.body || ""); setEditing(true); }} className="h-7 text-[#9a9a9a] hover:text-white"><PencilSimple size={13} className="mr-1" />Edit</Button>
                      )
                    )}
                    <Button data-testid="regenerate-btn" size="sm" variant="ghost" onClick={() => regen.mutate(detail.id)} disabled={regen.isPending}
                      className="h-7 text-[#A56CFF] hover:text-[#6B3DFF]"><ArrowsClockwise size={13} className={`mr-1 ${regen.isPending ? "ai-thinking" : ""}`} />{regen.isPending ? "Regenerating…" : "Regenerate"}</Button>
                  </div>
                </div>
                {editing ? (
                  <Textarea data-testid="edit-body-input" value={editBody} onChange={(e) => setEditBody(e.target.value)} rows={6}
                    className="bg-[#10101A] border-white/10 text-sm" />
                ) : (
                  detail.body ? <div className="text-sm text-[#cfcfcf] whitespace-pre-wrap bg-[#10101A] border border-white/8 rounded-lg p-3">{detail.body}</div>
                    : <div className="text-xs text-[#5a5a5a]">{(detail.deliverable_type || detail.type) === "image" ? "Use Regenerate to (re)create this image." : "No content yet."}</div>
                )}
              </div>

              {detail.meta?.reel_storyboard && (
                <div className="rounded-xl border border-pink-500/25 bg-pink-500/[0.05] p-3.5" data-testid="detail-reel-storyboard">
                  <div className="label-caps text-[9px] text-pink-300 mb-2">Reel storyboard — record this, then publish from your phone</div>
                  {detail.meta.reel_storyboard.hook && <div className="text-sm font-semibold mb-2">Hook: <span className="font-normal text-[#cfcfcf]">{detail.meta.reel_storyboard.hook}</span></div>}
                  <div className="space-y-1.5">
                    {(detail.meta.reel_storyboard.scenes || []).map((s, i) => (
                      <div key={i} className="text-xs text-[#cfcfcf] flex gap-2">
                        <span className="shrink-0 h-5 w-5 rounded-full bg-pink-500/20 text-pink-300 text-[10px] font-bold flex items-center justify-center">{s.scene || i + 1}</span>
                        <span>{s.action}{s.text_overlay ? <span className="text-[#9a9a9a]"> — overlay: “{s.text_overlay}”</span> : null}</span>
                      </div>
                    ))}
                  </div>
                  {detail.meta.reel_storyboard.audio_suggestion && <div className="mt-2 text-[11px] text-[#9a9a9a]">🎵 Audio: {detail.meta.reel_storyboard.audio_suggestion}</div>}
                  {detail.meta?.requires_video && <div className="mt-2 text-[10px] text-amber-300/90">Auto Pilot won't publish this until you record the video — it stays here waiting for you.</div>}
                </div>
              )}

              {Array.isArray(detail.meta?.carousel_slides) && detail.meta.carousel_slides.length > 0 && (
                <div className="rounded-xl border border-sky-500/25 bg-sky-500/[0.05] p-3.5" data-testid="detail-carousel-slides">
                  <div className="label-caps text-[9px] text-sky-300 mb-2">Carousel — {detail.meta.carousel_slides.length} slides (images come from your Media Library)</div>
                  <div className="space-y-1.5">
                    {detail.meta.carousel_slides.map((s, i) => (
                      <div key={i} className="text-xs text-[#cfcfcf] flex gap-2">
                        <span className="shrink-0 h-5 w-5 rounded-full bg-sky-500/20 text-sky-300 text-[10px] font-bold flex items-center justify-center">{s.slide || i + 1}</span>
                        <span>{s.heading ? <b>{s.heading}: </b> : null}{s.text}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Comments */}
              <div data-testid="comments-section">
                <div className="flex items-center gap-1.5 label-caps mb-2"><ChatCircleDots size={13} /> Internal comments</div>
                <div className="space-y-2 mb-2">
                  {(detail.comments || []).map((c, i) => (
                    <div key={i} className="bg-white/[0.03] border border-white/8 rounded-lg px-3 py-2">
                      <div className="text-sm text-[#cfcfcf] whitespace-pre-wrap">{c.text}</div>
                      <div className="text-[10px] text-[#7a7a7a] mt-1">{new Date(c.at).toLocaleString()}</div>
                    </div>
                  ))}
                  {!(detail.comments || []).length && <div className="text-xs text-[#5a5a5a]">No comments yet.</div>}
                </div>
                <div className="flex gap-2">
                  <Textarea data-testid="comment-input" value={comment} onChange={(e) => setComment(e.target.value)} rows={2}
                    placeholder="Add a note for your team…" className="bg-[#10101A] border-white/10 text-sm" />
                  <Button data-testid="add-comment-btn" onClick={addComment} className="bg-[#6B3DFF] hover:bg-[#A56CFF] text-white self-end">Add</Button>
                </div>
              </div>

              {/* Version history */}
              <div data-testid="history-section">
                <div className="flex items-center gap-1.5 label-caps mb-2"><ClockCounterClockwise size={13} /> Version history</div>
                <div className="space-y-1.5">
                  {(detail.history || []).slice().reverse().map((h, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-[#9a9a9a]">
                      <span className="h-1.5 w-1.5 rounded-full bg-[#6B3DFF] shrink-0" />
                      <span className="text-[#cfcfcf]">{STATUS_LABEL[h.from] || h.from || "created"} → {STATUS_LABEL[h.to] || h.to}</span>
                      <span className="text-[10px] text-[#7a7a7a] ml-auto">{new Date(h.at).toLocaleString()}</span>
                    </div>
                  ))}
                  {!(detail.history || []).length && <div className="text-xs text-[#5a5a5a]">No status changes recorded yet.</div>}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <ImproveCompareDialog data={qcCompare} onClose={() => setQcCompare(null)} onKeepImproved={keepImprovedQc} />

      {/* 🚀 Campaign Readiness — final agency review */}
      <Dialog open={!!readiness} onOpenChange={(o) => { if (!o) setReadiness(null); }}>
        <DialogContent className="bg-[#14141F] border-white/10 text-white max-w-lg" data-testid="readiness-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><RocketLaunch size={18} className="text-sky-400" weight="fill" /> Campaign Readiness</DialogTitle>
            <DialogDescription className="sr-only">Final agency-style review score with per-check breakdown and suggestions.</DialogDescription>
          </DialogHeader>
          {readiness && (
            <div className="space-y-4">
              <div className="text-center">
                <div className={`text-5xl font-head font-bold ${readiness.overall >= 85 ? "text-emerald-400" : readiness.overall >= 70 ? "text-amber-400" : "text-red-400"}`} data-testid="readiness-overall">
                  {readiness.overall}<span className="text-xl text-[#7a7a7a]">/100</span>
                </div>
                {readiness.verdict && <p className="text-xs text-[#9a9a9a] mt-1 italic">"{readiness.verdict}"</p>}
              </div>
              <div className="grid grid-cols-2 gap-1.5" data-testid="readiness-checks">
                {(readiness.checks || []).map((c) => (
                  <div key={c.label} className="flex items-center justify-between rounded-lg border border-white/8 px-2.5 py-1.5">
                    <span className="flex items-center gap-1.5 text-[11px] text-[#cfcfcf]">
                      {c.pass ? <CheckCircle size={13} className="text-emerald-400" weight="fill" /> : <Warning size={13} className="text-amber-400" weight="fill" />}
                      {c.label}
                    </span>
                    <span className={`text-[11px] font-bold ${c.pass ? "text-emerald-400" : "text-amber-400"}`}>{c.score}</span>
                  </div>
                ))}
              </div>
              {(readiness.suggestions || []).length > 0 && (
                <div className="rounded-lg bg-white/[0.03] border border-white/8 p-3" data-testid="readiness-suggestions">
                  <div className="text-[10px] label-caps text-[#7a7a7a] mb-1.5">Suggestions</div>
                  {readiness.suggestions.map((s, i) => <div key={i} className="text-[11px] text-[#9a9a9a] leading-snug">• {s}</div>)}
                </div>
              )}
              <div className="flex justify-end gap-2 flex-wrap">
                <Button data-testid="readiness-review-again" variant="outline" onClick={() => setReadiness(null)} className="border-white/15 bg-transparent text-white hover:bg-white/5">Review Again</Button>
                <Button data-testid="readiness-auto-optimize" onClick={() => { const it = readiness.item; setReadiness(null); runQcImprove(it); }}
                  className="bg-[#6B3DFF]/15 hover:bg-[#6B3DFF]/25 text-[#C39BFF] border border-[#6B3DFF]/30">
                  <Sparkle size={14} className="mr-1" weight="fill" /> Auto Optimize
                </Button>
                <Button data-testid="readiness-publish-anyway" onClick={publishAnyway} className="bg-emerald-600 hover:bg-emerald-500 text-white">
                  <Rocket size={14} className="mr-1" weight="fill" /> Publish Anyway
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Revision history — compare improvement passes over time */}
      <Dialog open={!!historyItem} onOpenChange={(o) => { if (!o) setHistoryItem(null); }}>
        <DialogContent className="bg-[#14141F] border-white/10 text-white max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="revision-history-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ClockCounterClockwise size={18} className="brand-text" /> Revision History — {historyItem?.title}</DialogTitle>
            <DialogDescription className="sr-only">All improvement passes for this design over time.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {(historyItem?.revisions || []).slice().reverse().map((rev, i) => {
              const od = rev.original_score?.overall, nd = rev.improved_score?.overall;
              return (
                <div key={rev.id || i} className="rounded-lg border border-white/8 p-3" data-testid={`revision-entry-${i}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[11px] text-[#7a7a7a]">{(rev.at || "").slice(0, 16).replace("T", " ")}</span>
                    <span className="flex items-center gap-2 text-[11px]">
                      <span className="text-[#9a9a9a]">{od ?? "—"}</span><ArrowRight size={11} className="text-[#555]" />
                      <span className={nd > od ? "text-emerald-400 font-bold" : "text-[#cfcfcf] font-semibold"}>{nd ?? "—"}</span>
                      {rev.kept && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400">KEPT</span>}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <img src={`${BACKEND}${rev.original_image_path}`} alt="original" className="w-full h-28 object-cover rounded opacity-70" />
                    <img src={`${BACKEND}${rev.improved_image_path}`} alt="improved" className="w-full h-28 object-cover rounded" />
                  </div>
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
